import {
  ApiJob,
  getJobEmoji,
  getJobPriorityColorClasses,
  getJobPriorityTitle,
  JobPriority,
} from "@/types/recruit/job.types.ts";
import {
  Avatar,
  Box,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSub,
  FontAwesomeIcon,
  ListItem,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

interface SidebarRecruitmentJobItemProps {
  jobItem: ApiJob;
  url: string;
  isActive: boolean;
  showPriorityStripe: boolean;
  showPinIndicator: boolean;
  isPinned: boolean;
  canPinJob: boolean;
  canEditJob: boolean;
  isMenuOpen: boolean;
  onMenuOpenChange: (jobId: number, open: boolean) => void;
  onEditJob: (jobItem: ApiJob) => void;
  onTogglePin: (jobItem: ApiJob) => void;
  onJobPriorityChange: (jobItem: ApiJob, priority: JobPriority | null) => void;
}

const jobPriorityOptions: Array<JobPriority | null> = [
  null,
  JobPriority.low,
  JobPriority.medium,
  JobPriority.high,
];

export const SidebarRecruitmentJobItem = ({
  jobItem,
  url,
  isActive,
  showPriorityStripe,
  showPinIndicator,
  isPinned,
  canPinJob,
  canEditJob,
  isMenuOpen,
  onMenuOpenChange,
  onEditJob,
  onTogglePin,
  onJobPriorityChange,
}: SidebarRecruitmentJobItemProps) => {
  const { t } = useTranslation();
  const listItemBaseClasses = "py-1 px-4 hover:bg-light hover:border-r";
  const activeClasses = "bg-light border-r";

  return (
    <div className={"relative"}>
      {showPriorityStripe && (
        <span
          className={twMerge(
            "absolute left-0 top-0 bottom-px w-[3px] z-20 pointer-events-none",
            getJobPriorityColorClasses(jobItem.priority).stripeBgClass
          )}
        />
      )}
      <ListItem
        to={url}
        titleAttribute={jobItem.title}
        titleSlot={
          <div className={"flex items-center overflow-hidden"}>
            <span className={"whitespace-nowrap text-ellipsis overflow-hidden min-w-0 text-title"}>
              {jobItem.title}
            </span>
            {showPinIndicator && isPinned ? (
              <FontAwesomeIcon icon="fa-light fa-thumbtack-angle" className={"shrink-0 ml-1"} />
            ) : undefined}
          </div>
        }
        caption={t("plurals:plural_candidates", { count: jobItem.candidates_count })}
        icon={
          canEditJob ? (
            <DropdownMenu
              open={isMenuOpen}
              onOpenChange={(open) => onMenuOpenChange(jobItem.id, open)}
              trigger={
                <Box
                  className={twMerge(
                    "relative group/job-menu size-9 rounded-sm",
                    isMenuOpen ? "bg-dark/5" : ""
                  )}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <Avatar
                    emoji={getJobEmoji(jobItem)}
                    size={"md"}
                    className={twMerge(
                      "rounded-sm",
                      isMenuOpen ? "hidden" : "group-hover/job-menu:hidden"
                    )}
                  />
                  <Avatar
                    size={"md"}
                    icon={<FontAwesomeIcon icon={"fa-light fa-ellipsis"} />}
                    className={twMerge(
                      "absolute inset-0",
                      isMenuOpen ? "flex" : "hidden group-hover/job-menu:flex"
                    )}
                  />
                </Box>
              }
            >
              <DropdownMenuItem
                title={t("common:edit")}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
                onClick={() => onEditJob(jobItem)}
              />
              {canPinJob && (
                <DropdownMenuItem
                  title={isPinned ? t("common:unpin") : t("common:pin")}
                  leftIcon={
                    <FontAwesomeIcon
                      icon={`fa-light ${isPinned ? "fa-thumbtack-angle-slash" : "fa-thumbtack-angle"}`}
                    />
                  }
                  onClick={() => onTogglePin(jobItem)}
                />
              )}
              {showPriorityStripe && (
                <DropdownMenuSub
                  title={t("ats:set_priority")}
                  leftIcon={
                    <FontAwesomeIcon
                      icon={"fa-solid fa-circle"}
                      className={getJobPriorityColorClasses(jobItem.priority).dotTextClass}
                    />
                  }
                >
                  {jobPriorityOptions.map((priorityOption) => (
                    <DropdownMenuItem
                      key={`job-priority-${jobItem.id}-${priorityOption ?? "not-defined"}`}
                      title={getJobPriorityTitle(priorityOption)}
                      leftIcon={
                        <FontAwesomeIcon
                          icon={"fa-solid fa-circle"}
                          className={getJobPriorityColorClasses(priorityOption).dotTextClass}
                        />
                      }
                      isSelected={jobItem.priority === priorityOption}
                      onClick={() => onJobPriorityChange(jobItem, priorityOption)}
                    />
                  ))}
                </DropdownMenuSub>
              )}
            </DropdownMenu>
          ) : (
            <Avatar emoji={getJobEmoji(jobItem)} size={"md"} />
          )
        }
        className={twMerge(listItemBaseClasses, isActive ? activeClasses : "")}
        truncate={true}
      />
    </div>
  );
};
